import { useCallback, useMemo, useState } from 'react';
import {
  SegmentationControllerType,
  SegmentationProps,
  segmentManagerLabelOrder,
} from 'config/analysisConfig';
import { configStore } from 'config/configStore';
import { rgbArrayToHex, rgbToHex } from 'config/colorUtils';
import { scheduleRender } from '../../rendering/Panels/renderState';
import { changeSegmentLabel } from '../../segmentManager';
import {
  initialcolorandopacity,
  updateLabelmapSegment,
} from '../../rendering/Panels/Renderers/CornerstoneManager';

type SegmentationMap = Record<string, SegmentationProps>;

type ColorMenuSnapshot = {
  key: SegmentationControllerType;
  initialVisibility: boolean;
};

export function useSegmentationVisibilityController({
  segmentation,
  showKeys,
  selectedSeriesId,
  manualMode,
  hasVR,
  analysisType,
  updatePanelState,
}: {
  segmentation: SegmentationMap;
  showKeys: SegmentationControllerType[];
  selectedSeriesId?: string | null;
  manualMode: boolean;
  hasVR: boolean;
  analysisType: string;
  updatePanelState: (...args: any[]) => void;
}) {
  const segKeys = Object.keys(segmentation);
  const selectedInitKey = showKeys.length > 0 ? showKeys[0] : segKeys[0];
  const index = Object.keys(segmentation).findIndex((k) => k === selectedInitKey);
  const [selectedKey, setSelectedKey] = useState<SegmentationControllerType>(
    segKeys[index] as SegmentationControllerType,
  );
  const [colorMenuOpen, setColorMenuOpen] = useState(false);
  const [colorMenuSnapshot, setColorMenuSnapshot] =
    useState<ColorMenuSnapshot | null>(null);

  const colorOptions = useMemo(
    () => Object.values(segmentation).map((s) => s.color),
    [segmentation],
  );

  const orderMap = useMemo(
    () =>
      new Map<string, number>(
        segmentManagerLabelOrder.map((key, orderIndex) => [key, orderIndex]),
      ),
    [],
  );

  const sorted = useMemo(
    () =>
      Object.entries(segmentation)
        .sort(([a], [b]) => {
          return (orderMap.get(a) ?? Infinity) - (orderMap.get(b) ?? Infinity);
        })
        .filter(([key]) => showKeys.includes(key as SegmentationControllerType)),
    [orderMap, segmentation, showKeys],
  );

  const handleSetSelectedKey = useCallback(
    (key: SegmentationControllerType) => {
      const targetSegmentation = segmentation[key];
      if (!targetSegmentation) return;

      setSelectedKey(key);
      if (manualMode && selectedSeriesId) {
        changeSegmentLabel(key);
        const colorHex = rgbArrayToHex(targetSegmentation.color);
        scheduleRender('Axial', { type: 'uknowuknow', colorHex });
        scheduleRender('Coronal', { type: 'uknowuknow', colorHex });
        scheduleRender('Sagittal', { type: 'uknowuknow', colorHex });
      }
    },
    [manualMode, segmentation, selectedSeriesId],
  );

  const handleSegmentation = useCallback(
    (
      key: keyof SegmentationProps,
      value: SegmentationProps[keyof SegmentationProps],
      target?: SegmentationControllerType,
    ) => {
      const targetKey = target ?? selectedKey;
      if (!selectedSeriesId) return;
      let { color, opacity, visibility } = segmentation[targetKey];
      switch (key) {
        case 'opacity':
          opacity = value as number;
          visibility = opacity !== 0;
          break;
        case 'visibility':
          const vis = value as boolean;
          if (vis && opacity === 0) {
            opacity = 1;
          }
          visibility = vis;
          break;
        case 'color':
          color = value as [number, number, number];
          break;
      }
      const newSegmentation = {
        ...segmentation,
        [targetKey]: {
          color,
          opacity,
          visibility,
        },
      };
      updateLabelmapSegment({
        segmentTarget: targetKey,
        segmentations: newSegmentation,
      });

      updatePanelState(targetKey, 'segmentation', {
        ...newSegmentation,
      });

      if (manualMode) {
        if (targetKey !== selectedKey) return;
        requestAnimationFrame(() => {
          handleSetSelectedKey(selectedKey);
        });
        return;
      }
      const keyTemp = target ?? selectedKey;
      const colorData = configStore.get().colors;
      if (keyTemp === 'coronary') {
        colorData.LUMEN = rgbToHex(color[0], color[1], color[2]);
      }
      if (keyTemp === 'calcium') {
        colorData.CALCIUM = rgbToHex(color[0], color[1], color[2]);
      }
      if (keyTemp === 'aorta') {
        colorData.aorta = rgbToHex(color[0], color[1], color[2]);
      }
      if (keyTemp === 'avc') {
        colorData.avc = rgbToHex(color[0], color[1], color[2]);
      }
      configStore.setData({ colors: colorData });

      initialcolorandopacity(analysisType, keyTemp, newSegmentation);
      scheduleRender('Surface3D', {
        type: 'updateSegmentation',
        name: keyTemp,
        newSegmentation,
      });
      if (key === 'visibility') {
        scheduleRender('VR', {
          type: 'updateSegmentation',
          name: keyTemp,
          newSegmentation,
        });
      }
    },
    [
      analysisType,
      handleSetSelectedKey,
      manualMode,
      selectedKey,
      selectedSeriesId,
      segmentation,
      updatePanelState,
    ],
  );

  const handleColorMenuClose = useCallback(() => {
    if (colorMenuSnapshot) {
      const currentVisibility = segmentation[colorMenuSnapshot.key].visibility;
      if (currentVisibility !== colorMenuSnapshot.initialVisibility && hasVR) {
        scheduleRender('VR', {
          type: 'updateSegmentation',
          name: colorMenuSnapshot.key,
          newSegmentation: segmentation,
        });
      }
    }
    setColorMenuSnapshot(null);
    setColorMenuOpen(false);
  }, [colorMenuSnapshot, hasVR, segmentation]);

  const handleColorPick = useCallback(
    (color: [number, number, number]) => {
      handleSegmentation('color', color);
      handleColorMenuClose();
    },
    [handleColorMenuClose, handleSegmentation],
  );

  const handleToggleShow = useCallback(
    (key: SegmentationControllerType) =>
      handleSegmentation('visibility', !segmentation[key].visibility, key),
    [handleSegmentation, segmentation],
  );

  const handleColorMenuToggle = useCallback(
    (key: SegmentationControllerType) => {
      if (colorMenuOpen) {
        handleColorMenuClose();
        return;
      }
      setColorMenuSnapshot({
        key,
        initialVisibility: segmentation[key].visibility,
      });
      setColorMenuOpen(true);
    },
    [colorMenuOpen, handleColorMenuClose, segmentation],
  );

  return {
    selectedKey,
    setSelectedKey,
    sorted,
    colorOptions,
    colorMenuOpen,
    handleSetSelectedKey,
    handleColorPick,
    handleToggleShow,
    handleColorMenuToggle,
    handleColorMenuClose,
  };
}
